<template>
  <q-btn v-if="isVisible" id="intercom-share" outline no-caps @click="dialogOpen = true">
    <q-icon v-if="showIcon" :name="shareIcon" size="medium" class="q-mr-sm" />
    <span v-if="$q.screen.gt.sm">Share</span>
    <q-dialog v-model="dialogOpen" transition-duration="100">
      <q-card style="width: 512px">
        <q-card-section>
          <div class="modal-title">Share "{{ report?.name }}"</div>
          <div class="text-grey-7">
            Allow colleagues and other stakeholders to view and analyze this report
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section>
          <email-dropdown-select
            id="intercom-share-add-people"
            v-model="invitePeopleList"
            :people-with-access="peopleWithAccess"
          />
          <template v-if="!invitePeopleList.length">
            <div class="section-title q-mt-md q-mb-sm">
              People with access ({{ filteredPeopleWithAccess.length }})
            </div>
            <q-markup-table flat separator="none" style="max-height: 275px; overflow-y: auto">
              <tbody>
                <tr v-for="u in filteredPeopleWithAccess" :key="u.id || u.email">
                  <td style="padding-left: 7px">
                    <div class="flex items-center">
                      <q-avatar size="32px" round class="q-mr-sm bg-grey-4">
                        <q-img
                          v-if="u?.picture"
                          :src="u.picture"
                          referrerpolicy="no-referrer"
                          no-spinner
                        />
                        <q-icon v-else name="person" />
                      </q-avatar>
                      <div class="column">
                        <div class="text-weight-medium">{{ userToName(u) }}</div>
                        <div style="font-size: 13px; color: #555">{{ u.email }}</div>
                      </div>
                    </div>
                  </td>
                  <td v-if="u.id !== report?.user_id" class="text-right" style="padding-right: 7px">
                    <span v-if="!u.id || u.id === u.email" class="text-grey-8 q-mr-sm">
                      <q-icon name="hourglass_top">
                        <q-tooltip>Waiting for an account to be created.</q-tooltip>
                      </q-icon>
                      <span class="cursor-pointer q-ml-sm">
                        <q-icon name="close" @click="removePeopleList.push(u.id || u.email)" />
                        <q-tooltip>Cancel invite</q-tooltip>
                      </span>
                    </span>
                    <q-btn-dropdown
                      v-else-if="!organizationMembers.some((_) => _.id === u.id)"
                      no-caps
                      flat
                      label="Viewer"
                    >
                      <q-item v-ripple clickable @click="removePeopleList.push(u.id || u.email)">
                        <q-item-section>Remove access</q-item-section>
                      </q-item>
                    </q-btn-dropdown>
                    <q-btn-dropdown v-else no-caps flat label="Editor">
                      <q-item
                        v-if="allowedToAdminOrOwner"
                        v-ripple
                        clickable
                        @click="pendingOwnershipChangeUserId = u.id || u.email"
                      >
                        <q-item-section>Transfer ownership</q-item-section>
                      </q-item>
                      <q-item v-ripple clickable @click="removePeopleList.push(u.id || u.email)">
                        <q-item-section>Remove access</q-item-section>
                      </q-item>
                    </q-btn-dropdown>
                  </td>
                  <td
                    v-else
                    class="text-right text-grey-8"
                    style="padding-right: 7px; font-size: 15px"
                  >
                    Owner
                  </td>
                </tr>
              </tbody>
            </q-markup-table>
            <template v-if="canMakePublic || canMakeCompanyWide">
              <div class="section-title q-mt-md">General access</div>
              <q-select
                id="intercom-share-general-access"
                v-model="shareSelection"
                transition-duration="100"
                outlined
                :options="shareOptions"
                :hint="shareSelection.hint"
                class="full-width q-my-sm"
                @update:model-value="shareSelectionUpdated"
              >
                <template #selected>
                  <div class="text-weight-medium">{{ shareSelection.label }}</div>
                </template>
                <template #prepend>
                  <q-avatar :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-3'" rounded>
                    <q-icon :name="shareSelection.icon" />
                  </q-avatar>
                </template>
                <template #option="scope">
                  <q-item v-bind="scope.itemProps">
                    <q-item-section avatar class="q-pr-none">
                      <q-avatar
                        :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-3'"
                        rounded
                        :icon="scope.opt.icon"
                      />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="text-weight-medium">{{ scope.opt.label }}</q-item-label>
                      <q-item-label
                        class="text-grey-8"
                        lines="3"
                        style="max-width: 400px; font-size: small"
                      >
                        {{ scope.opt.hint }}
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                </template>
              </q-select>
              <q-input
                v-if="shareSelection.index === 2"
                v-model="shareableLink"
                dense
                readonly
                class="q-mt-md"
              >
                <template #append>
                  <q-btn
                    round
                    unelevated
                    size="sm"
                    flat
                    no-caps
                    color="primary"
                    @click="copyShareableLink"
                  >
                    <q-icon :name="wasCopied ? 'check' : 'content_copy'" size="xs" />
                  </q-btn>
                </template>
              </q-input>
            </template>
          </template>
        </q-card-section>
        <q-separator />
        <q-card-actions v-if="!invitePeopleList.length" class="row justify-end q-gutter-x-sm">
          <i v-if="removePeopleList.length" class="q-my-auto text-grey-8">Pending changes</i>
          <q-btn
            id="intercom-share-done"
            color="primary"
            unelevated
            no-caps
            :loading="loading"
            @click="saveUpdates"
          >
            {{ removePeopleList.length ? 'Save' : 'Done' }}
          </q-btn>
        </q-card-actions>
        <q-card-actions v-else class="row justify-end q-gutter-x-sm">
          <q-btn flat no-caps color="primary" @click="invitePeopleList = []">Cancel</q-btn>
          <q-btn
            id="intercom-share-done"
            color="primary"
            unelevated
            no-caps
            :loading="loading"
            @click="invitePeople"
          >
            Invite
          </q-btn>
        </q-card-actions>
      </q-card>
      <q-dialog v-model="ownershipModal" persistent>
        <q-card style="width: 412px">
          <q-card-section class="row items-center">
            <div class="modal-title">Make this person the owner?</div>
            <span class="q-mt-md">
              You are about to transfer ownership of this property report. Are you sure you want to
              proceed?
            </span>
          </q-card-section>
          <q-card-actions align="right">
            <q-btn flat no-caps label="No" @click="pendingOwnershipChangeUserId = null" />
            <q-btn
              flat
              no-caps
              label="Yes"
              color="primary"
              @click="changeOwnership(pendingOwnershipChangeUserId!)"
            />
          </q-card-actions>
        </q-card>
      </q-dialog>
    </q-dialog>
  </q-btn>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { copyToClipboard } from 'quasar'
import { type RentSourceSubscriptionAttributes, type AccessUser, FeatureFlag } from 'common'

import { useUserStore } from '@/stores/user'
import { usePropertyReportStore } from '@/stores/propertyreport'
import EmailDropdownSelect from './EmailDropdownSelect.vue'

export default {
  name: 'ButtonShare',
  components: { EmailDropdownSelect },
  props: {
    report: {
      type: Object as PropType<RentSourceSubscriptionAttributes | null>,
      default: null
    },
    showIcon: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      dialogOpen: false,
      invitePeopleList: [] as AccessUser[],
      notifyPeople: true,
      pendingOwnershipChangeUserId: null as string | null,
      removePeopleList: [] as string[],
      peopleWithAccess: [] as AccessUser[],
      shareSelection: {
        index: 0,
        label: 'Restricted',
        hint: 'Private report. Optionally, you can share it with some specific emails by adding them to the list above.',
        value: [
          { column: 'is_public', value: false },
          { column: 'is_organization_visible', value: false }
        ],
        icon: 'lock_outline'
      },
      wasCopied: false,
      publicId: null as string | null,
      isOrganizationVisible: false,
      loading: false,
      currentEditValue: ''
    }
  },
  computed: {
    allowedToAdminOrOwner() {
      return useUserStore().allowedToAdmin(this.report?.user_id, true)
    },
    canMakePublic() {
      return (
        useUserStore().hasFeatureFlag(null, FeatureFlag.MAKE_PUBLIC) ||
        useUserStore().allowedToAdmin(this.report?.user_id, false)
      )
    },
    canMakeCompanyWide() {
      return (
        useUserStore().hasFeatureFlag(null, FeatureFlag.MAKE_ORGANIZATION_VISIBLE) ||
        useUserStore().allowedToAdmin(this.report?.user_id, false)
      )
    },
    ownershipModal() {
      return this.pendingOwnershipChangeUserId !== null
    },
    user() {
      return useUserStore().user
    },
    isVisible() {
      if (this.report) {
        return this.organizationMembers.some((_) => _.id === this.report?.user_id)
      }
      return false
    },
    shareIcon() {
      if (this.shareSelection.icon === 'lock_outline' && this.peopleWithAccess.length < 2) {
        return 'share'
      }
      return this.shareSelection.icon === 'lock_outline' && this.peopleWithAccess.length > 1
        ? 'group'
        : this.shareSelection.icon
    },
    filteredPeopleWithAccess() {
      return this.peopleWithAccess.filter((_) => !this.removePeopleList.includes(_.id || _.email))
    },
    shareOptions() {
      const allOptions = [
        {
          index: 0,
          label: 'Restricted',
          hint: 'Private report. Optionally, you can share it with some specific emails by adding them to the list above.',
          value: [
            { column: 'is_public', value: false },
            { column: 'is_organization_visible', value: false }
          ],
          icon: 'lock_outline'
        }
      ]

      const org = useUserStore().organization

      if (org && this.canMakeCompanyWide) {
        allOptions.push({
          index: 1,
          label: org.display_name,
          hint: `Company-wide report. All ${this.organizationMembers.length} members of ${org.display_name} will be able to view it on their dashboard. Share with additional people by adding them to the list above.`,
          value: [
            { column: 'is_public', value: false },
            { column: 'is_organization_visible', value: true }
          ],
          icon: 'business'
        })
      }

      if (this.canMakePublic) {
        allOptions.push({
          index: 2,
          label: 'Public',
          hint: org
            ? `Visible to everyone in ${org.display_name} and anyone having access to the public link.`
            : 'Visible to everyone having access to the public link.',
          value: [
            { column: 'is_public', value: true },
            { column: 'is_organization_visible', value: true }
          ],
          icon: 'public'
        })
      }

      return allOptions
    },
    shareableLink(): string {
      if (this.publicId) {
        return `${window.location.origin}/property-reports/public/${this.publicId}`
      }
      return ''
    },
    organizationMembers() {
      return useUserStore().organizationMembers
    }
  },
  watch: {
    dialogOpen() {
      if (!this.dialogOpen) {
        this.resetModal()
      }
    },
    report() {
      this.reportChanged()
    },
    invitePeopleList() {
      if (this.removePeopleList.length) {
        this.saveUpdates()
      }
    }
  },
  created() {
    this.reportChanged()
  },
  methods: {
    reportChanged() {
      if (this.report) {
        this.publicId = this.report.public_id
        this.isOrganizationVisible = this.report.is_organization_visible
        this.updateShareOption()
        if (this.report?.id) {
          usePropertyReportStore()
            .getAccess(this.report.id)
            .then((access) => {
              this.peopleWithAccess = access
            })
        }
      }
    },
    userToName(user: AccessUser): string {
      const suffix = user.id === this.$auth0.user.value?.sub ? ' (you)' : ''
      if (user.given_name && user.family_name) {
        return `${user.given_name} ${user.family_name}${suffix}`
      }
      return `${user.email}${suffix}`
    },
    copyShareableLink() {
      copyToClipboard(this.shareableLink).then(() => {
        this.wasCopied = true
        setTimeout(() => {
          this.wasCopied = false
        }, 1000)
      })
    },
    async changeOwnership(toUserId: string) {
      const currentOwner = this.peopleWithAccess.find((_) => _.id === this.report?.user_id)
      if (this.report?.id && toUserId && currentOwner && currentOwner.id && currentOwner?.email) {
        this.loading = true

        // add the current owner to the list of people with access
        await usePropertyReportStore().createAccess(this.report.id, false, [
          { user_id: currentOwner.id, email: currentOwner.email }
        ])

        // switch the owner
        await usePropertyReportStore().update({
          ...this.report,
          user_id: toUserId
        })

        // remove the new owner from the list of people with access
        await usePropertyReportStore()
          .deleteAccess(this.report.id, toUserId)
          .finally(() => {
            this.loading = false
            this.pendingOwnershipChangeUserId = null
          })

        // TODO: remove the side effect of mutating the report prop
        // it works, but it's not great
        this.report.user_id = toUserId
        usePropertyReportStore().updateAccessUsers(this.report, this.peopleWithAccess)
      }
    },
    updateShareOption() {
      if (this.publicId) {
        const newShareVal = this.shareOptions.find((_) => _.index === 2)
        if (newShareVal) {
          this.shareSelection = newShareVal
        }
      } else if (this.isOrganizationVisible) {
        const newShareVal = this.shareOptions.find((_) => _.index === 1)
        if (newShareVal) {
          this.shareSelection = newShareVal
        }
      }
    },
    shareSelectionUpdated(visibility: {
      index: number
      label: string
      hint: string
      value: { column: 'is_public' | 'is_organization_visible'; value: boolean }[]
      icon: string
    }) {
      const isPublic = visibility.value.find((_) => _.column === 'is_public')?.value || false
      const isOrganizationVisible =
        visibility.value.find((_) => _.column === 'is_organization_visible')?.value || false

      const reportId = this.report?.id
      if (reportId) {
        usePropertyReportStore()
          .update({
            ...this.report,
            is_organization_visible: isOrganizationVisible,
            public_id: isPublic.toString()
          })
          .then((response) => {
            if (response) {
              this.publicId = response.public_id
              this.isOrganizationVisible = response.is_organization_visible
              this.updateShareOption()

              let mode: 'private' | 'public' | 'organization' = 'private'
              if (isPublic) {
                mode = 'public'
              } else if (isOrganizationVisible) {
                mode = 'organization'
              }

              this.$tracker.changedReportVisibility(reportId, 'property', mode, 'property_report')
            }
          })
      }
    },
    async saveUpdates() {
      if (this.report?.id) {
        this.loading = true
        await Promise.all(
          this.removePeopleList.map((userId) =>
            usePropertyReportStore().deleteAccess(this.report!.id, userId)
          )
        )
          .then(() => {
            this.peopleWithAccess = this.peopleWithAccess.filter(
              (_) => !this.removePeopleList.includes(_.id || _.email)
            )
            if (this.removePeopleList.length > 0) {
              this.$tracker.changedReportVisibility(
                this.report.id,
                'property',
                'removed',
                'property_report'
              )
            }
            this.resetModal()
            usePropertyReportStore().updateAccessUsers(this.report, this.peopleWithAccess)
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    async invitePeople() {
      let savePromise = Promise.resolve()
      if (this.invitePeopleList.length && this.report?.id) {
        this.loading = true
        savePromise = usePropertyReportStore()
          .createAccess(
            this.report.id,
            this.notifyPeople,
            this.invitePeopleList.map((_) => ({ user_id: _.id, email: _.email }))
          )
          .then((response) => {
            this.peopleWithAccess = response
            if (this.report?.id) {
              this.$tracker.changedReportVisibility(
                this.report.id,
                'property',
                'email',
                'property_report'
              )

              usePropertyReportStore().updateAccessUsers(this.report, this.peopleWithAccess)
            }
          })
          .finally(() => {
            this.loading = false
          })
      }
      await savePromise.then(() => {
        this.resetModal()
        useUserStore().initPeopleInOrgOrShared()
      })
    },
    resetModal() {
      this.invitePeopleList = []
      this.notifyPeople = true
      this.removePeopleList = []
      this.dialogOpen = false
    }
  }
}
</script>

<style scoped>
.section-title {
  font-weight: 450;
  font-size: 16px;
}
.modal-title {
  font-size: 20px;
  font-weight: 400;
}
</style>
